import React, { useEffect, useState } from "react";
import { Col, Row, Space, Spin, Table, Typography, Tooltip, Modal, Button, Dropdown, message, notification, Empty } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { PlusOutlined, InfoCircleOutlined, MoreOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import Card from "antd/es/card/Card";
import { PrimaryButton } from "../shared/button/Buttons";
import { TestSet } from "../../model_gen/evaluation";
import { Link } from "react-router-dom";
import { TestSetService } from "../../services/TestSetService";
import { NEW_TEST_SET_STORAGE_KEY } from "./TestSetNew";
import { IntentService } from "../../services/IntentService";
import { IntentGroup } from "../../model_gen/intent";

type TestSetColumns = ColumnsType<TestSet>;

const TestSets: React.FC = () => {
  const { apiKey } = usePermissions();
  const { intentGroupId } = useParams();
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);
  const [cachedTestSets, setCachedTestSets] = useState<Record<number, TestSet[]>>({});
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const navigate = useNavigate();

  const styles = {
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
      transition: "all 0.3s ease",
    },
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
    detailsHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
  };

  // Add global CSS for interactive rows
  useEffect(() => {
    // Add styles for interactive rows
    const style = document.createElement('style');
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .interactive-row {
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
      .ant-table {
        border-radius: 8px;
        overflow: hidden;
      }
      .ant-tag {
        border-radius: 4px;
        padding: 3px 8px;
      }
      .test-sets-container .ant-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
      }
      .test-sets-container .ant-btn {
        transition: all 0.2s ease;
      }
      .ant-pagination-item-active {
        border-color: #1890ff;
        font-weight: 500;
      }
      .ant-pagination-item:hover {
        border-color: #1890ff;
      }
      .ant-table-pagination {
        margin: 16px 0;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(Number(intentGroupId), false, setIsLoading, apiKey).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  const handleDeleteTestSet = (testSet: TestSet) => {
    Modal.confirm({
      title: "Delete Test Set",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete test set "${testSet.name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        TestSetService.deleteTestSet(testSet.id.toString(), null, apiKey).subscribe(
          () => {
            message.success({
              content: "Test set deleted successfully",
              style: { marginTop: '20px' },
            });
            // Remove the deleted test set from the current page
            const updatedTestSets = testSets.filter(ts => ts.id !== testSet.id);
            setTestSets(updatedTestSets);
            // Update the cache
            setCachedTestSets(prev => ({
              ...prev,
              [currentPage]: updatedTestSets
            }));
            // Update total count
            setTotalCount(prev => prev - 1);
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: `Failed to delete test set: ${error}`,
            });
          }
        );
      },
    });
  };

  const testSetColumns: TestSetColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
      render: (text: string) => (
        <Typography.Text style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: false,
      width: "30%",
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word", color: text ? '#262626' : '#8c8c8c' }}>
          {text || "No description provided"}
        </div>
      ),
    },
    {
      title: (
        <Space>
          Golden
          <Tooltip title="Test sets marked as 'Golden' will be used to evaluate the performance of models after fine-tuning">
            <InfoCircleOutlined
              style={{
                color: "rgba(0, 0, 0, 0.45)",
                fontSize: "14px",
              }}
            />
          </Tooltip>
        </Space>
      ),
      dataIndex: "is_golden",
      key: "is_golden",
      width: "15%",
      render: (isGolden: boolean) => (
        <Typography.Text 
          style={{ color: isGolden ? "#52c41a" : "#8c8c8c", fontWeight: isGolden ? 500 : 400 }}
        >
          {isGolden ? "Yes" : "No"}
        </Typography.Text>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (text: number) => {
        const date = moment(text);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) => moment(a.date_created).unix() - moment(b.date_created).unix(),
    },
    {
      title: "# Requests",
      dataIndex: ["meta", "request_count"],
      key: "request_count",
      width: "15%",
      render: (count: number) => (
        <Typography.Text 
          strong 
          style={{ color: count > 0 ? "#52c41a" : "#8c8c8c" }}
        >
          {count}
        </Typography.Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: "48px",
      render: (_, record: TestSet) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleDeleteTestSet(record);
                },
              },
            ],
          }}
          trigger={["click"]}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const fetchTestSets = (page: number) => {
    if (cachedTestSets[page]) {
      setTestSets(cachedTestSets[page]);
      return;
    }

    const offset = (page - 1) * pageSize;
    setIsLoading(true);
    if (intentGroupId) {
      TestSetService.getTestSetsByIntentGroup(
        intentGroupId,
        offset,
        pageSize,
        setIsLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          
          setTestSets(response.items);
          setTotalCount(response.total);
          setCachedTestSets(prev => ({
            ...prev,
            [page]: response.items
          }));
          setIsLoading(false);
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    }
  };

  useEffect(() => {
    setCachedTestSets({});
    setCurrentPage(1);
  }, [intentGroupId]);

  useEffect(() => {
    if (intentGroupId) {
      fetchTestSets(currentPage);
    }
  }, [intentGroupId, currentPage, apiKey]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  if (isLoading && !testSets.length) {
    return (
      <div className="loading-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 200px)" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="test-sets-container" style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                {intentGroup ? (
                  <>
                    <Link to="/intent-groups">Intent Groups</Link> / <Link to={`/intent-group/${intentGroupId}`}>{intentGroup.intent_name}</Link> / Test Sets
                  </>
                ) : (
                  <Spin size="small" />
                )}
              </Typography.Text>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 600 }}>
                Test Sets
              </Typography.Title>
              {intentGroup && (
                <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                  Manage test sets for evaluating model performance
                </Typography.Text>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <div style={styles.contentCard}>
        {/* Table Header with Button */}
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Test Sets
          </Typography.Title>
          <PrimaryButton
            title="New Test Set"
            className="buttonWrapper"
            icon={<PlusOutlined />}
            onClick={() => {
              localStorage.removeItem(NEW_TEST_SET_STORAGE_KEY);
              navigate(`/intent-group/${intentGroupId}/testsets/new`);
            }}
          />
        </div>
        <div style={styles.tableContainer}>
          <Table
            rowKey={(record) => record.id.toString()}
            columns={testSetColumns}
            dataSource={testSets}
            loading={isLoading}
            rowClassName={() => "interactive-row"}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalCount,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} items`,
            }}
            onChange={handleTableChange}
            onRow={(record) => {
              return {
                onClick: () => navigate(`/intent-group/${intentGroupId}/testsets/${record.id}`),
                style: { cursor: "pointer" }
              };
            }}
            locale={{
              emptyText: (
                <Empty 
                  description={
                    <div>
                      <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
                        No test sets found
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        Create a new test set to evaluate your model's performance.
                      </Typography.Text>
                    </div>
                  } 
                  image={Empty.PRESENTED_IMAGE_SIMPLE} 
                />
              )
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TestSets;
